import React, { lazy, Suspense } from 'react';
import { Provider } from 'react-redux';
import { Navigate, Route, Routes } from 'react-router';
import { HubspotProvider } from '@aaronhayes/react-use-hubspot-form';
import { createGlobalStyle } from 'styled-components';
import { setupStore } from 'shared/store/store';
import { PATHS } from 'shared/consts/paths';
import HomePage from 'pages/HomePage/HomePage';
import ErrorPage from 'pages/app/ErrorPage/ErrorPage';
import Layout from 'widgets/Layout/Layout';
import ScrollToTop from 'shared/lib/hoc/ScrollToTop';
import { ErrorBoundary } from 'shared/ui';

// Lazy-load all pages except HomePage to reduce initial bundle size.
// App-flow pages (upload, check, report, coupon, download)
const UploadPage = lazy(() => import('pages/app/UploadPage/UploadPage'));
const CheckPage = lazy(() => import('pages/app/CheckPage/CheckPage'));
const ReportPage = lazy(() => import('pages/app/ReportPage/ReportPage'));
const CouponPage = lazy(() => import('pages/app/CouponPage/CouponPage'));
const DownloadPage = lazy(() => import('pages/app/DownloadPage/DownloadPage'));
// Legal
const TermsOfUsePage = lazy(() => import('pages/legal/TermsOfUsePage/TermsOfUsePage'));
const PrivacyPolicyPage = lazy(() => import('pages/legal/PrivacyPolicyPage/PrivacyPolicyPage'));
const AboutPage = lazy(() => import('pages/legal/AboutPage/AboutPage'));
const ContactPage = lazy(() => import('pages/legal/ContactPage/ContactPage'));
// SEO pages
const VoipCheckerPage = lazy(() => import('pages/seo/VoipCheckerPage/VoipCheckerPage'));
const HowToVerifyPhoneNumberPage = lazy(
    () => import('pages/seo/HowToVerifyPhoneNumberPage/HowToVerifyPhoneNumberPage'),
);
const BulkPhoneValidationPage = lazy(() => import('pages/seo/BulkPhoneValidationPage/BulkPhoneValidationPage'));
const CarrierLookupPage = lazy(() => import('pages/seo/CarrierLookupPage/CarrierLookupPage'));
const FreePhoneLookupPage = lazy(() => import('pages/seo/FreePhoneLookupPage/FreePhoneLookupPage'));
const HowToCleanSmsListPage = lazy(() => import('pages/seo/HowToCleanSmsListPage/HowToCleanSmsListPage'));
const CheckMobileOrLandlinePage = lazy(() => import('pages/seo/CheckMobileOrLandlinePage/CheckMobileOrLandlinePage'));
const WhatIsVoipNumberPage = lazy(() => import('pages/seo/WhatIsVoipNumberPage/WhatIsVoipNumberPage'));
const CanYouTextLandlinePage = lazy(() => import('pages/seo/CanYouTextLandlinePage/CanYouTextLandlinePage'));
const IsThisARealPhonePage = lazy(() => import('pages/seo/IsThisARealPhonePage/IsThisARealPhonePage'));
const WhatIsLandlinePage = lazy(() => import('pages/seo/WhatIsLandlinePage/WhatIsLandlinePage'));
const ResourcesPage = lazy(() => import('pages/ResourcesPage/ResourcesPage'));
const PricingPage = lazy(() => import('pages/PricingPage/PricingPage'));
const GlossaryIndexPage = lazy(() => import('pages/glossary/GlossaryIndexPage/GlossaryIndexPage'));
const GlossaryTermPage = lazy(() => import('pages/glossary/GlossaryTermPage/GlossaryTermPage'));
const PhoneCheckrAlternativesPage = lazy(
    () => import('pages/seo/PhoneCheckrAlternativesPage/PhoneCheckrAlternativesPage'),
);
const PhoneNumberValidationApiPage = lazy(
    () => import('pages/seo/PhoneNumberValidationApiPage/PhoneNumberValidationApiPage'),
);
const UsAreaCodesPage = lazy(() => import('pages/seo/UsAreaCodesPage/UsAreaCodesPage'));
const PhoneValidationRealEstatePage = lazy(
    () => import('pages/seo/PhoneValidationRealEstatePage/PhoneValidationRealEstatePage'),
);
const PhoneValidationEcommercePage = lazy(
    () => import('pages/seo/PhoneValidationEcommercePage/PhoneValidationEcommercePage'),
);
const PhoneNumberScrubberPage = lazy(() => import('pages/seo/PhoneNumberScrubberPage/PhoneNumberScrubberPage'));
const PhoneNumberEnrichmentPage = lazy(() => import('pages/seo/PhoneNumberEnrichmentPage/PhoneNumberEnrichmentPage'));
const PhoneValidationInsurancePage = lazy(
    () => import('pages/seo/PhoneValidationInsurancePage/PhoneValidationInsurancePage'),
);
const PhoneValidationRecruitingPage = lazy(
    () => import('pages/seo/PhoneValidationRecruitingPage/PhoneValidationRecruitingPage'),
);
const PhoneValidationHealthcarePage = lazy(
    () => import('pages/seo/PhoneValidationHealthcarePage/PhoneValidationHealthcarePage'),
);
const PhoneValidationDebtCollectionPage = lazy(
    () => import('pages/seo/PhoneValidationDebtCollectionPage/PhoneValidationDebtCollectionPage'),
);
const PhoneNumberVerificationToolPage = lazy(
    () => import('pages/seo/PhoneNumberVerificationToolPage/PhoneNumberVerificationToolPage'),
);
const FreePhoneNumberValidationPage = lazy(
    () => import('pages/seo/FreePhoneNumberValidationPage/FreePhoneNumberValidationPage'),
);
const FreePhoneNumberCheckerPage = lazy(
    () => import('pages/seo/FreePhoneNumberCheckerPage/FreePhoneNumberCheckerPage'),
);
const ChangelogPage = lazy(() => import('pages/ChangelogPage/ChangelogPage'));
// Localhost-only design preview surfaces (see PATHS.DESIGN*).
const DesignIndexPage = lazy(() => import('pages/design/DesignIndexPage/DesignIndexPage'));
const DesignV1Page = lazy(() => import('pages/design/DesignV1Page/DesignV1Page'));
const DesignV2Page = lazy(() => import('pages/design/DesignV2Page/DesignV2Page'));
const DesignV3Page = lazy(() => import('pages/design/DesignV3Page/DesignV3Page'));
const isLocalhost =
    typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

const store = setupStore();

const GlobalStyle = createGlobalStyle`
  html {
    height: 100%;
  }

  body {
    margin: 0;
    /* 'Inter Fallback' is a size-adjusted Arial (see public/index.html) that
       matches Inter's box metrics so text doesn't reflow when the web font
       finally swaps in. Prevents FOUT-caused CLS. */
    font-family: 'Inter', 'Inter Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
    font-size: 14px;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    overflow-x: hidden;
  }

  h1, h2, h3, h4, h5, h6 {
    font-family: 'Manrope', 'Manrope Fallback', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    font-weight: 700;
  }

  code {
    font-family: source-code-pro, Menlo, Monaco, Consolas, 'Courier New',
    monospace;
  }

  img {
    max-width: 100%;
  }

  figure {
    margin: 0;
  }

  a {
    cursor: pointer;
    color: inherit;

    &:hover {
      text-decoration: none;
    }
  }
  
  ul, 
  ol {
    list-style: none;
  }

  p {
    font-weight: 400;
  }

  input,
  textarea,
  button,
  a {
    &:focus {
      outline: 2px solid #2563eb;
      outline-offset: 2px;
      text-decoration: none;
    }

    &:focus:not(:focus-visible) {
      outline: none;
    }

    &:focus-visible {
      outline: 2px solid #2563eb;
      outline-offset: 2px;
    }
  }

  hr {
    margin-top: 30px;
    margin-bottom: 30px;
  }

  * {
    box-sizing: border-box;
  }
`;

const App = () => {
    return (
        <Provider store={store}>
            <GlobalStyle />
            <HubspotProvider>
                <ErrorBoundary>
                    <ScrollToTop>
                        {/*
                          Route-level Suspense reserves viewport height while
                          a lazy-loaded page chunk is fetched, so real users
                          don't see a 0px collapse → full page shift on route
                          transitions and first load. aria-hidden keeps the
                          placeholder out of the accessibility tree.
                        */}
                        <Suspense fallback={<div aria-hidden="true" style={{ minHeight: '100vh' }} />}>
                            <Routes>
                                <Route path={PATHS.HOME} element={<Layout />}>
                                    <Route path={PATHS.ERROR_PAGE} element={<ErrorPage />} />
                                    <Route index element={<HomePage />} />
                                    <Route path={PATHS.UPLOAD} element={<UploadPage />} />
                                    <Route path={PATHS.CHECK} element={<CheckPage />} />
                                    <Route path={PATHS.REPORT} element={<ReportPage />} />
                                    <Route path={PATHS.COUPON} element={<CouponPage />} />
                                    <Route path={PATHS.TERMS_OF_USE} element={<TermsOfUsePage />} />
                                    <Route path={PATHS.PRIVACY_POLICY} element={<PrivacyPolicyPage />} />
                                    <Route path={PATHS.ABOUT} element={<AboutPage />} />
                                    <Route path={PATHS.CONTACT} element={<ContactPage />} />
                                    <Route path={PATHS.DOWNLOAD} element={<DownloadPage />} />
                                    <Route path={PATHS.VOIP_CHECKER} element={<VoipCheckerPage />} />
                                    <Route path={PATHS.HOW_TO_VERIFY_PHONE} element={<HowToVerifyPhoneNumberPage />} />
                                    <Route path={PATHS.BULK_PHONE_VALIDATION} element={<BulkPhoneValidationPage />} />
                                    <Route path={PATHS.CARRIER_LOOKUP} element={<CarrierLookupPage />} />
                                    <Route path={PATHS.FREE_PHONE_LOOKUP} element={<FreePhoneLookupPage />} />
                                    <Route path={PATHS.HOW_TO_CLEAN_SMS_LIST} element={<HowToCleanSmsListPage />} />
                                    <Route
                                        path={PATHS.CHECK_MOBILE_OR_LANDLINE}
                                        element={<CheckMobileOrLandlinePage />}
                                    />
                                    <Route path={PATHS.WHAT_IS_VOIP_NUMBER} element={<WhatIsVoipNumberPage />} />
                                    <Route path={PATHS.CAN_YOU_TEXT_LANDLINE} element={<CanYouTextLandlinePage />} />
                                    <Route path={PATHS.IS_THIS_A_REAL_PHONE} element={<IsThisARealPhonePage />} />
                                    <Route path={PATHS.WHAT_IS_LANDLINE} element={<WhatIsLandlinePage />} />
                                    <Route path={PATHS.RESOURCES} element={<ResourcesPage />} />
                                    <Route path={PATHS.PRICING} element={<PricingPage />} />
                                    <Route path={PATHS.GLOSSARY} element={<GlossaryIndexPage />} />
                                    <Route path={PATHS.GLOSSARY_TERM} element={<GlossaryTermPage />} />
                                    <Route path="/blog" element={<Navigate to={PATHS.RESOURCES} replace />} />
                                    <Route path={PATHS.ALTERNATIVES} element={<PhoneCheckrAlternativesPage />} />
                                    <Route
                                        path={PATHS.PHONE_VALIDATION_API}
                                        element={<PhoneNumberValidationApiPage />}
                                    />
                                    <Route path={PATHS.US_AREA_CODES} element={<UsAreaCodesPage />} />
                                    <Route path={PATHS.REAL_ESTATE} element={<PhoneValidationRealEstatePage />} />
                                    <Route path={PATHS.ECOMMERCE} element={<PhoneValidationEcommercePage />} />
                                    <Route path={PATHS.PHONE_SCRUBBER} element={<PhoneNumberScrubberPage />} />
                                    <Route path={PATHS.PHONE_ENRICHMENT} element={<PhoneNumberEnrichmentPage />} />
                                    <Route path={PATHS.INSURANCE} element={<PhoneValidationInsurancePage />} />
                                    <Route path={PATHS.RECRUITING} element={<PhoneValidationRecruitingPage />} />
                                    <Route path={PATHS.HEALTHCARE} element={<PhoneValidationHealthcarePage />} />
                                    <Route
                                        path={PATHS.DEBT_COLLECTION}
                                        element={<PhoneValidationDebtCollectionPage />}
                                    />
                                    <Route
                                        path={PATHS.PHONE_VERIFICATION_TOOL}
                                        element={<PhoneNumberVerificationToolPage />}
                                    />
                                    <Route
                                        path={PATHS.FREE_PHONE_VALIDATION}
                                        element={<FreePhoneNumberValidationPage />}
                                    />
                                    <Route path={PATHS.FREE_PHONE_CHECKER} element={<FreePhoneNumberCheckerPage />} />
                                    {isLocalhost && <Route path={PATHS.CHANGELOG} element={<ChangelogPage />} />}
                                    {isLocalhost && <Route path={PATHS.DESIGN} element={<DesignIndexPage />} />}
                                    {isLocalhost && <Route path={PATHS.DESIGN_V1} element={<DesignV1Page />} />}
                                    {isLocalhost && <Route path={PATHS.DESIGN_V2} element={<DesignV2Page />} />}
                                    {isLocalhost && <Route path={PATHS.DESIGN_V3} element={<DesignV3Page />} />}
                                </Route>
                            </Routes>
                        </Suspense>
                    </ScrollToTop>
                </ErrorBoundary>
            </HubspotProvider>
        </Provider>
    );
};

export default App;
